import { Text } from "styles/utility-components";

import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import Badge from "lib/core_components/Badge";
import MultiSelectDropdown from "lib/core_components/MultiSelectDropdownList";
import { Team, useTeams } from "lib/hooks/api/teams/useTeams";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "lib/themes/colors";

type Props = {
  onChange: (teamIds: string[]) => void;
  teamIds: string[];
  minified?: boolean;
};

export function TeamDropdownFilter({ teamIds, onChange, minified = false }: Props) {
  const currentUser = useAssertCurrentUser();
  const currentTeamId = currentUser.team.id;

  const { data: teams, isLoading: isLoadingTeam } = useTeams();

  const currentTeam = teams?.find((team) => team.id === currentTeamId);
  const otherTeams = teams?.filter((team) => team.id !== currentTeamId) ?? [];

  // Display the user's current team at the top of the list
  const companyTeams = currentTeam ? [currentTeam, ...otherTeams] : [...otherTeams];
  const teamOptions = companyTeams.map((team) => ({ title: team.name, value: team.id })) ?? [];

  // getSelectDropdownLabel returns null for a team id that no longer resolves to a team.
  const label = getSelectDropdownLabel(teamIds, companyTeams, currentTeamId) ?? "";

  return (
    <MultiSelectDropdown
      title={
        <>
          <UIcon icon="users01" color={sysPrimaryDefault} />
          {!minified && (
            // Only fires when the team name is actually cut off, so the everyday labels
            // don't get a tooltip repeating what is already on screen.
            <EllipsisTooltip fullText={label}>
              {(ref) => (
                <Text ref={ref} color="inherit" ellipsis>
                  {label}
                </Text>
              )}
            </EllipsisTooltip>
          )}
          {minified && teamIds.length > 0 && <Badge size={16}>{teamIds.length}</Badge>}
        </>
      }
      fieldName="team"
      searchable
      hideSelectAll
      skipSort
      options={teamOptions}
      selectedOptions={teamIds}
      onChange={onChange}
      loading={isLoadingTeam}
      showSelectedSizeBadge={false}
      hideChevron={minified}
    />
  );
}

function getSelectDropdownLabel(
  selectedTeamIds: string[],
  allTeams: Team[],
  currentTeamId: string,
) {
  const numSelectedTeams = selectedTeamIds.length;
  const totalTeams = allTeams.length;

  const getTeamNameById = (id: string) => {
    const team = allTeams.find((team) => team.id === id);
    return team ? team.name : null;
  };

  if (numSelectedTeams === 0) return "All teams";

  if (numSelectedTeams === 1) {
    return selectedTeamIds[0] === currentTeamId ? "My team" : getTeamNameById(selectedTeamIds[0]);
  }

  if (numSelectedTeams < totalTeams) {
    return `${numSelectedTeams} teams`;
  }

  return "All teams";
}
